/**
 * InlineInput - Compact form input for inline editing contexts.
 *
 * A styled input component for use in tables, cards, and compact forms.
 * Supports size variants and error states with consistent styling.
 *
 * @example
 * ```tsx
 * <InlineInput
 *   size="xs"
 *   error={!!errors.value}
 *   placeholder="0"
 *   {...register("value")}
 * />
 * ```
 */

import { forwardRef, type InputHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@lean-agility/design-system/react';

const inlineInputVariants = cva(
  `
    w-full rounded-sm border bg-transparent text-text-primary transition-colors
    placeholder:text-text-muted
    focus:border-transparent focus:ring-1 focus:ring-primary-500 focus:outline-hidden
    disabled:cursor-not-allowed disabled:opacity-50
  `,
  {
    variants: {
      size: {
        xs: 'h-7 px-1.5 text-xs', // 28px - metadata, number inputs
        sm: 'h-8.5 px-2 text-sm font-medium', // 34px - feature names
        md: 'h-8.5 px-2 text-base font-medium', // 34px - deliverable names
      },
      error: {
        true: `
          border-accent-rose
          focus:border-accent-rose
        `,
        false: `
          border-border-strong/50
          hover:border-border-input-hover
        `,
      },
      align: {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
      },
    },
    defaultVariants: {
      size: 'sm',
      error: false,
      align: 'left',
    },
  },
);

export interface InlineInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, VariantProps<typeof inlineInputVariants> {}

export const InlineInput = forwardRef<HTMLInputElement, InlineInputProps>(
  ({ className, size, error, align, ...props }, ref) => {
    return <input className={cn(inlineInputVariants({ size, error, align, className }))} ref={ref} {...props} />;
  },
);

InlineInput.displayName = 'InlineInput';
